import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const categories = [
  { key: 'shoes', icon: 'ri-footprint-line' },
  { key: 'hoodies', icon: 'ri-t-shirt-line' },
  { key: 'tshirts', icon: 'ri-t-shirt-2-line' },
  { key: 'jackets', icon: 'ri-shirt-line' },
  { key: 'pants', icon: 'ri-shirt-line' },
  { key: 'headwear', icon: 'ri-landscape-line' },
  { key: 'sets', icon: 'ri-stack-line' },
  { key: 'underwear', icon: 'ri-vip-diamond-line' },
  { key: 'jersey', icon: 'ri-shirt-fill' },
  { key: 'accessories', icon: 'ri-key-2-line' },
  { key: 'bags', icon: 'ri-shopping-bag-3-line' },
  { key: 'belts', icon: 'ri-link' },
  { key: 'perfumes', icon: 'ri-drop-line' },
  { key: 'other', icon: 'ri-more-line' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [showMobileCategories, setShowMobileCategories] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentLang = i18n.language || 'pt';
  const isLangPage = (lang: string) => location.pathname === `/${lang}/` || (lang === 'pt' && location.pathname === '/');

  // Determine if we are on a home page (transparent hero background)
  const isHomePage = location.pathname === '/' || location.pathname === '/pt/' || location.pathname === '/de/' || location.pathname === '/en/';

  // Navbar is only transparent (white text) on the homepage hero at the very top.
  // Everywhere else it uses a solid background so the text stays visible.
  const isTransparent = isHomePage && !isScrolled && !isMobileMenuOpen;

  const homeHref = currentLang === 'pt' ? '/' : `/${currentLang}/`;
  const prefix = currentLang === 'pt' ? '' : currentLang + '/';

  const navLinks = [
    { label: t('nav_home'), href: homeHref },
    { label: t('nav_spreadsheet'), href: `/${prefix}cssbuy-spreadsheet/` },
    { label: t('nav_categories'), href: `/${prefix}categories/` },
    { label: t('nav_shopping_guide'), href: `/${prefix}guides/` },
    { label: t('nav_shipping'), href: `/${prefix}shipping/` },
    { label: t('nav_reviews'), href: `/${prefix}reviews/` },
    { label: t('nav_safety'), href: '/safety/' },
  ];

  const languages = [
    { code: 'pt', label: 'PT', path: '/' },
    { code: 'de', label: 'DE', path: '/de/' },
    { code: 'en', label: 'EN', path: '/en/' },
  ];

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    const target = lang === 'pt' ? '/' : `/${lang}/`;
    navigate(target);
    setIsLangDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleCategoryClick = (key: string) => {
    const url = t(`cat_url_${key}`);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={homeHref} className="flex items-center gap-2 group">
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors ${
              isTransparent ? 'text-white' : 'text-foreground-950'
            }`}>
              CSSBuy<span className="text-primary-500">BR</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 ${
                  isTransparent ? 'text-white/90' : 'text-foreground-700'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isTransparent
                    ? 'text-white/90 hover:bg-white/10'
                    : 'text-foreground-700 hover:bg-background-200'
                }`}
              >
                {currentLang.toUpperCase()}
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center" />
              </button>
              {isLangDropdownOpen && (
                <div className="absolute right-0 mt-2 w-24 bg-background-50 rounded-lg shadow-lg border border-background-200 py-1 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${
                        isLangPage(lang.code) ? 'text-primary-500 font-semibold' : 'text-foreground-700'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Button */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('open_spreadsheet')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-md transition-colors ${
              isTransparent ? 'text-white' : 'text-foreground-700'
            }`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center text-lg`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors py-2"
              >
                {link.label}
              </a>
            ))}

            {/* Mobile categories */}
            <div className="pt-3 border-t border-background-200">
              <button
                onClick={() => setShowMobileCategories(!showMobileCategories)}
                className="flex items-center justify-between w-full py-2 text-sm font-semibold text-foreground-900"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  {t('nav_categories')}
                </span>
                <i className={`${showMobileCategories ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} w-4 h-4 flex items-center justify-center text-foreground-400`} />
              </button>
              {showMobileCategories && (
                <div className="grid grid-cols-2 gap-2 pb-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => handleCategoryClick(cat.key)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-100 text-left hover:bg-primary-50 transition-colors"
                    >
                      <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500 shrink-0`} />
                      <span className="text-xs text-foreground-700 truncate">{t(`category_${cat.key}`)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language switcher */}
            <div className="flex items-center gap-2 pt-3 border-t border-background-200">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => switchLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isLangPage(lang.code)
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center px-4 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors"
            >
              {t('open_spreadsheet')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}